import { TGlobalStores } from '@/editor/stores';
import { CommonController2d } from '../CommonController';
import { calMousePosition, mouseVectorToWorldPosition } from '@/editor/utils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { IFurniture } from '@/editor/data/model/furniture';
import { modelManager } from '@/editor/manager/entityManager';
import { EArrayPlaceMode, EEntityType } from '@/editor/types';
import { EDoorWindowSide, IWindowData } from '@/editor/data/model/model';
import { IAssemblyData } from '@/editor/data/assembly/assembly';
import { ArrayAbsorbManager } from '@/editor/manager/absorb/array/arrayAbsorbManager';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { VectorPointUtils } from '@/editor/math/VectorPointUtils';
import { WallBaseData } from '@/editor/data/wall/wallBaseData';

interface IArrayDataBase {
  xLimit?: {
    positive: number;
    negative: number;
  };
  mode: EArrayPlaceMode;
}

type _TEntityData =
  | {
      entityType: EEntityType.Furniture;
      data: IFurniture;
    }
  | {
      entityType: EEntityType.DoorWindow;
      data: IWindowData;
    }
  | {
      entityType: EEntityType.Assembly;
      data: IAssemblyData;
    };

type TArrayEntityData = _TEntityData & IArrayDataBase;

export class OperateArrayController extends CommonController2d {
  #updateRafId: number | null = null;
  #latestMousePos: IPoint2d | null = null;
  #adsorbManager: ArrayAbsorbManager | null;
  #arrayEntityData: TArrayEntityData | null;

  constructor(globalStores: TGlobalStores) {
    super(globalStores);
    this.#arrayEntityData = this.#getArrayEntityData();
    if (!this.#arrayEntityData) {
      this.#adsorbManager = null;
      this.dispose();
      this.exit();
      return;
    }
    this.#adsorbManager = this.#arrayEntityData.mode === EArrayPlaceMode.Line ? null : new ArrayAbsorbManager();
    modelManager.arrayPreviewManager.create({ ...this.#arrayEntityData, onConfirm: this.#confirmArray });
  }

  override init() {
    super.init();
  }

  /** 获取从 operateInfo 获取阵列布置需要的数据 */
  #getArrayEntityData(): TArrayEntityData | null {
    if (this._gs.operateInfo.extraState?.arrayData) {
      const { entityId, entityType } = this._gs.operateInfo.extraState.arrayData;
      switch (entityType) {
        case EEntityType.Furniture: {
          const boxData = this._gs.modelStore.getFurnitureData(entityId);
          if (!boxData) {
            break;
          }
          return { entityType, data: boxData, mode: EArrayPlaceMode.Matrix };
        }
        case EEntityType.Assembly: {
          const boxData = this._gs.assemblyStore.getAssemblyData(entityId);
          if (!boxData) {
            break;
          }
          return { entityType: entityType, data: boxData, mode: EArrayPlaceMode.Matrix };
        }
        case EEntityType.DoorWindow: {
          const data = this._gs.modelStore.getWindowData(entityId);
          if (!data) {
            break;
          }
          const { wallType = EEntityType.Wall, relativeWallId } = data.relativeWallInfo;
          // TODO: 弧形墙暂不支持阵列，后续需要支持
          let relativeWallData: Pick<WallBaseData, 'start' | 'end'> | undefined;
          if (wallType === EEntityType.SeparateWall) {
            const wallData = this._gs.separateWallStore.getSepData(relativeWallId, false);
            if (wallData && wallData.center === undefined) {
              relativeWallData = wallData;
            }
          } else {
            const wall = this._gs.wallStore.getWallData(relativeWallId);
            if (wall && wall.baseData.center === undefined) {
              relativeWallData = wall.baseData;
            }
          }

          if (!relativeWallData) {
            break;
          }

          const startDistance = Point2dUtils.distanceTo(relativeWallData.start, data.center);
          const endDistance = Point2dUtils.distanceTo(relativeWallData.end, data.center);
          const xLimit =
            data.side === EDoorWindowSide.left
              ? { positive: endDistance, negative: startDistance }
              : { positive: startDistance, negative: endDistance };
          return { entityType: entityType, data: data, xLimit, mode: EArrayPlaceMode.Line };
        }
      }
    }
    return null;
  }

  protected override mouseMove(e: MouseEvent): void {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    this.#latestMousePos = VectorPointUtils.vectorToPoint(calMousePosition(e, renderer.domElement));

    if (this.#updateRafId !== null) return;

    this.#updateRafId = requestAnimationFrame(() => {
      this.#updateRafId = null;
      const mousePos = this.#latestMousePos;
      this.#latestMousePos = null;
      const data = this.#arrayEntityData;
      if (!mousePos || !data) return;

      const mouseWorldPos = mouseVectorToWorldPosition(mousePos, camera);

      const absorbMousePos =
        this.#adsorbManager?.calculateAbsorbPointThenRender(mouseWorldPos, data.data) ?? mouseWorldPos;
      modelManager.arrayPreviewManager.update({ mousePos: absorbMousePos });
    });
  }

  #confirmArray = () => {
    const arrayGrid = modelManager.arrayPreviewManager.getGridData();
    if (arrayGrid && this.#arrayEntityData) {
      switch (this.#arrayEntityData.entityType) {
        case EEntityType.Furniture: {
          this._gs.furnitureOperation.generateFurnitureArray(this.#arrayEntityData.data, arrayGrid);
          break;
        }
        case EEntityType.DoorWindow: {
          this._gs.windowDoorOperation.generateDoorWindowArray(this.#arrayEntityData.data, arrayGrid);
          break;
        }
        case EEntityType.Assembly: {
          this._gs.assemblyOperation.generateAssemblyArray(this.#arrayEntityData.data, arrayGrid);
          break;
        }
      }
      this._gs.editorStateStore.clearSelected();
    }
    this.dispose();
    this.exit();
  };

  protected override mouseUp(e: MouseEvent, dragged: boolean): void {
    if (dragged) return;
    if (e.button === 0) {
      this.#confirmArray();
    } else if (e.button === 2) {
      // 右键点击退出
      this.dispose();
      this.exit();
    }
  }

  override dispose() {
    if (this.#updateRafId !== null) {
      cancelAnimationFrame(this.#updateRafId);
      this.#updateRafId = null;
    }
    this.#latestMousePos = null;
    modelManager.arrayPreviewManager.clear();
    this.#arrayEntityData = null;
    this.#adsorbManager?.dispose();
    this.#adsorbManager = null;
    super.dispose();
  }
}
